import { IllegalActionError } from "../plugin.js";
import { BOARD_SIZE, CENTER, pointsFor, premiumAt, } from "./board.js";
export function emptyBoard() {
    return Array.from({ length: BOARD_SIZE }, () => Array.from({ length: BOARD_SIZE }, () => null));
}
export function isEmpty(board) {
    return board.every((row) => row.every((cell) => cell === null));
}
const inBounds = (value) => value >= 0 && value < BOARD_SIZE;
/**
 * Sprawdza kształt ruchu i zwraca kierunek.
 *
 * Wymagania: pola puste, wszystkie w jednej linii, bez dziur po scaleniu
 * z płytkami już leżącymi, pierwszy ruch przez środek, kolejne stykające się
 * z czymś, co już jest.
 */
export function validateShape(board, placements) {
    if (placements.length === 0)
        throw new IllegalActionError("Nie wyłożyłeś żadnej płytki.");
    const seen = new Set();
    for (const placement of placements) {
        if (!inBounds(placement.row) || !inBounds(placement.column)) {
            throw new IllegalActionError("Płytka poza planszą.");
        }
        if (board[placement.row]?.[placement.column]) {
            throw new IllegalActionError("To pole jest już zajęte.");
        }
        const key = `${placement.row}:${placement.column}`;
        if (seen.has(key))
            throw new IllegalActionError("Dwie płytki na tym samym polu.");
        seen.add(key);
    }
    const rows = new Set(placements.map((p) => p.row));
    const columns = new Set(placements.map((p) => p.column));
    if (rows.size > 1 && columns.size > 1) {
        throw new IllegalActionError("Płytki muszą leżeć w jednym rzędzie albo w jednej kolumnie.");
    }
    // Jedna płytka jest jednocześnie pozioma i pionowa — kierunek rozstrzyga to,
    // z czym się styka. Domyślnie poziomo; `collectWords` i tak zbada obie osie.
    const kierunek = rows.size === 1 && columns.size === 1
        ? "poziomo"
        : rows.size === 1
            ? "poziomo"
            : "pionowo";
    // Ciągłość: między skrajnymi wyłożonymi płytkami nie może być pustego pola.
    const stale = kierunek === "poziomo" ? [...rows][0] : [...columns][0];
    const zmienne = placements
        .map((p) => (kierunek === "poziomo" ? p.column : p.row))
        .sort((a, b) => a - b);
    for (let value = zmienne[0]; value <= zmienne[zmienne.length - 1]; value++) {
        const row = kierunek === "poziomo" ? stale : value;
        const column = kierunek === "poziomo" ? value : stale;
        const wylozona = placements.some((p) => p.row === row && p.column === column);
        if (!wylozona && !board[row]?.[column]) {
            throw new IllegalActionError("Między płytkami nie może być pustego pola.");
        }
    }
    if (isEmpty(board)) {
        const przezSrodek = placements.some((p) => p.row === CENTER && p.column === CENTER);
        if (!przezSrodek)
            throw new IllegalActionError("Pierwsze słowo musi przechodzić przez środek.");
        if (placements.length < 2)
            throw new IllegalActionError("Pierwsze słowo musi mieć co najmniej dwie litery.");
    }
    else {
        const styka = placements.some((p) => [
            [p.row - 1, p.column],
            [p.row + 1, p.column],
            [p.row, p.column - 1],
            [p.row, p.column + 1],
        ].some(([r, c]) => inBounds(r) && inBounds(c) && board[r]?.[c]));
        if (!styka)
            throw new IllegalActionError("Słowo musi stykać się z płytkami już leżącymi.");
    }
    return kierunek;
}
/** Plansza z naniesionymi płytkami ruchu — bez modyfikowania oryginału. */
export function withPlacements(board, placements) {
    const next = board.map((row) => [...row]);
    for (const placement of placements) {
        const blank = placement.tile === "?";
        const letter = (blank ? (placement.as ?? "") : placement.tile).toLowerCase();
        if (letter === "")
            throw new IllegalActionError("Blank musi mieć wybraną literę.");
        next[placement.row][placement.column] = { letter, blank };
    }
    return next;
}
/** Skrajny początek słowa w danym kierunku. */
function wordStart(board, row, column, pionowo) {
    let index = pionowo ? row : column;
    while (index > 0) {
        const r = pionowo ? index - 1 : row;
        const c = pionowo ? column : index - 1;
        if (!board[r]?.[c])
            break;
        index -= 1;
    }
    return index;
}
/** Odczytuje słowo od podanego początku; zwraca `null`, gdy ma jedną literę. */
function readWord(board, row, column, pionowo) {
    const start = wordStart(board, row, column, pionowo);
    const tiles = [];
    let letters = "";
    for (let index = start; index < BOARD_SIZE; index++) {
        const r = pionowo ? index : row;
        const c = pionowo ? column : index;
        const cell = board[r]?.[c];
        if (!cell)
            break;
        letters += cell.letter;
        tiles.push({ row: r, column: c });
    }
    // Pojedyncza litera to nie słowo — istotne dla płytek, które nic
    // prostopadłego nie tworzą.
    return letters.length >= 2 ? { word: letters, tiles } : null;
}
/**
 * Wszystkie słowa powstałe w ruchu: główne plus każde prostopadłe.
 *
 * Słowa prostopadłe to najczęściej pomijana część reguł — a przy gęstej
 * planszy jeden ruch potrafi utworzyć ich pięć i wszystkie muszą być poprawne.
 */
export function collectWords(board, placements, kierunek) {
    const wyniki = [];
    const widziane = new Set();
    const dodaj = (found) => {
        if (!found)
            return;
        const klucz = found.tiles.map((t) => `${t.row}:${t.column}`).join(",");
        if (widziane.has(klucz))
            return;
        widziane.add(klucz);
        wyniki.push(found);
    };
    const pierwsza = placements[0];
    dodaj(readWord(board, pierwsza.row, pierwsza.column, kierunek === "pionowo"));
    for (const placement of placements) {
        dodaj(readWord(board, placement.row, placement.column, kierunek !== "pionowo"));
    }
    if (wyniki.length === 0) {
        throw new IllegalActionError("Ten ruch nie tworzy żadnego słowa.");
    }
    return wyniki;
}
/**
 * Punktacja jednego słowa.
 *
 * Premie liczą się **wyłącznie od płytek wyłożonych w tym ruchu** — pole
 * premiowe zużywa się raz na zawsze. Blank jest wart zero niezależnie od tego,
 * jaką literę reprezentuje, ale premia za słowo pod nim nadal działa.
 */
export function scoreWord(board, tiles, swiezeIndeksy) {
    let suma = 0;
    let mnoznik = 1;
    for (const { row, column } of tiles) {
        const cell = board[row]?.[column];
        if (!cell)
            continue;
        const wartosc = cell.blank ? 0 : pointsFor(cell.letter);
        const swieza = swiezeIndeksy.has(`${row}:${column}`);
        const premia = swieza ? premiumAt(row, column) : "";
        if (premia === "P2")
            suma += wartosc * 2;
        else if (premia === "P3")
            suma += wartosc * 3;
        else
            suma += wartosc;
        if (premia === "S2")
            mnoznik *= 2;
        else if (premia === "S3")
            mnoznik *= 3;
    }
    return suma * mnoznik;
}
/**
 * Pełna punktacja ruchu: suma wszystkich słów plus premia za wyłożenie
 * wszystkich siedmiu płytek.
 */
export function scoreMove(boardAfter, placements, kierunek, bingoBonus, wSlowniku) {
    const swieze = new Set(placements.map((p) => `${p.row}:${p.column}`));
    const znalezione = collectWords(boardAfter, placements, kierunek);
    const words = znalezione.map((found) => ({
        word: found.word,
        points: scoreWord(boardAfter, found.tiles, swieze),
        inDictionary: wSlowniku(found.word),
        tiles: found.tiles,
    }));
    let points = words.reduce((total, word) => total + word.points, 0);
    if (placements.length === 7)
        points += bingoBonus;
    return { words, points };
}
/**
 * Zdejmuje ze stojaka płytki użyte w ruchu.
 *
 * Rzuca, gdy gracz próbuje wyłożyć płytkę, której nie ma — to jedyna
 * bariera przed dopisaniem sobie dowolnej litery przez spreparowany pakiet.
 */
export function takeFromRack(rack, placements) {
    const pozostale = [...rack];
    const used = [];
    for (const placement of placements) {
        const tile = placement.tile.toLowerCase();
        const index = pozostale.indexOf(tile);
        if (index === -1)
            throw new IllegalActionError(`Nie masz płytki „${tile}" na stojaku.`);
        pozostale.splice(index, 1);
        used.push(tile);
    }
    return { rack: pozostale, used };
}
/** Suma wartości płytek pozostałych na stojaku — rozliczenie końcowe. */
export function rackValue(rack) {
    return rack.reduce((total, tile) => total + pointsFor(tile), 0);
}
//# sourceMappingURL=rules.js.map